// ======================================================================
// File: TransportDrawer.js
// Module: PPE → Transport
// Purpose:
//   Drawer for creating Transport Request
//   FIX: Render using React Portal so it overlays entire PPE layout
// ======================================================================

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";

import transportService from "../../services/transportService";
import "../../css/transport.css";


// ======================================================================
// COMPONENT
// ======================================================================

export default function TransportDrawer({
  show,
  onClose,
  onCreated,
  editTransport = null,
  onUpdated,
}) {

  const [saving, setSaving] = useState(false);
  const isEditMode = Boolean(editTransport?.id);

  // ✅ FIX: ensure portal renders only after DOM mount
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  
const [form, setForm] = useState({
  request_type: "OUTBOUND",
  reference_type: "",
  reference_id: "",
  source_name: "",
  destination_name: "",
  expected_dispatch_datetime: "",
  expected_delivery_datetime: "",
  transporter_bp_id: null,
});


useEffect(() => {
  if (!show) return;

  if (editTransport) {
    const toDateTimeLocal = (value) => {
      if (!value) return "";

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) return "";

      const pad = (number) => String(number).padStart(2, "0");

      return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
      );
    };

    setForm({
      request_type: editTransport.request_type || "OUTBOUND",
      reference_type: editTransport.reference_type || "",
      reference_id: editTransport.reference_id ?? "",
      source_name: editTransport.source_name || "",
      destination_name: editTransport.destination_name || "",
      expected_dispatch_datetime: toDateTimeLocal(
        editTransport.expected_dispatch_datetime
      ),
      expected_delivery_datetime: toDateTimeLocal(
        editTransport.expected_delivery_datetime
      ),
      transporter_bp_id: editTransport.transporter_bp_id ?? null,
    });

    setTransporterSearch(
      editTransport.transporter_name_snapshot || ""
    );
    setPoSearch(
      editTransport.reference_type === "PURCHASE_ORDER"
        ? (editTransport.po_no || "")
        : ""
    );
    setPoOptions([]);
    setShowPoDropdown(false);

  } else {
    setForm({
      request_type: "OUTBOUND",
      reference_type: "",
      reference_id: "",
      source_name: "",
      destination_name: "",
      expected_dispatch_datetime: "",
      expected_delivery_datetime: "",
      transporter_bp_id: null,
    });

    setTransporterSearch("");
  }
}, [show, editTransport]);



  useEffect(() => {
  if (!show) return;

  const toDateTimeLocal = (value) => {
    if (!value) return "";
    return String(value).slice(0, 16);
  };

  if (editTransport) {
    setForm({
      request_type: editTransport.request_type || "OUTBOUND",
      reference_type: editTransport.reference_type || "",
      reference_id: editTransport.reference_id ?? "",
      source_name: editTransport.source_name || "",
      destination_name: editTransport.destination_name || "",
      expected_dispatch_datetime: toDateTimeLocal(
        editTransport.expected_dispatch_datetime
      ),
      expected_delivery_datetime: toDateTimeLocal(
        editTransport.expected_delivery_datetime
      ),
      transporter_bp_id: editTransport.transporter_bp_id ?? null,
    });

    setTransporterSearch(
      editTransport.transporter_name_snapshot || ""
    );
    setPoSearch(
      editTransport.reference_type === "PURCHASE_ORDER"
        ? (editTransport.po_no || "")
        : ""
    );
    setPoOptions([]);
    setShowPoDropdown(false);
    
  } else {
    setForm({
      request_type: "OUTBOUND",
      reference_type: "",
      reference_id: "",
      source_name: "",
      destination_name: "",
      expected_dispatch_datetime: "",
      expected_delivery_datetime: "",
      transporter_bp_id: null,
    });

    setTransporterSearch("");
  }

  setTransporterOptions([]);
  setShowDropdown(false);
}, [show, editTransport]);

  // ==============================================================
  // TRANSPORTER LOOKUP STATE
  // ==============================================================
  const [transporterSearch, setTransporterSearch] = useState("");
  const [transporterOptions, setTransporterOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  
const [poSearch, setPoSearch] = useState("");
const [poOptions, setPoOptions] = useState([]);
const [showPoDropdown, setShowPoDropdown] = useState(false);


  // ------------------------------------------------------------------
  // HANDLE CHANGE
  // ------------------------------------------------------------------
  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  // ------------------------------------------------------------------
  // SEARCH TRANSPORTERS
  // ------------------------------------------------------------------
  
  
const handlePurchaseOrderSearch = async (value) => {
  setPoSearch(value);

  // Clear the selected PO whenever the search text changes.
  setForm((prev) => ({
    ...prev,
    reference_id: "",
  }));

  if (!value || value.trim().length < 2) {
    setPoOptions([]);
    setShowPoDropdown(false);
    return;
  }

  try {
    const res = await transportService.searchPurchaseOrders(
      value.trim()
    );

    setPoOptions(res?.data || []);
    setShowPoDropdown(true);
  } catch (err) {
    console.error("Purchase Order lookup failed", err);
    setPoOptions([]);
    setShowPoDropdown(false);
  }
};

  const handleTransporterSearch = async (value) => {

    setTransporterSearch(value);

    if (!value || value.length < 2) {
      setTransporterOptions([]);
      setShowDropdown(false);
      return;
    }

    try {
      const res = await transportService.searchTransporters(value);
      setTransporterOptions(res?.data || []);
      setShowDropdown(true);
    } catch (err) {
      console.error("Transporter lookup failed", err);
    }
  };

  // ------------------------------------------------------------------
  // SAVE
  // ------------------------------------------------------------------
  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = {
        ...form,
        transporter_bp_id: form.transporter_bp_id || null,
      };

      if (isEditMode) {
        await transportService.updateTransport(
          editTransport.id,
          payload
        );
      } else {
        if (isEditMode) {
            await transportService.updateTransport(editTransport.id, payload);
          } else {
            await transportService.createTransport(payload);
          }
      }

      if (onCreated) onCreated();

    
    } catch (err) {
      //console.error(err);
      console.error("Failed to save transport request:", err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to save transport request";
      //alert("Failed to create transport");
      alert(message);
    
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------------
  // DO NOT RENDER IF CLOSED OR NOT MOUNTED
  // ------------------------------------------------------------------
  if (!mounted || !show) return null;

  // ------------------------------------------------------------------
  // PORTAL RENDER
  // ------------------------------------------------------------------
  return createPortal(
    <div className="drawer-overlay">
      <div className="drawer-panel">

        {/* ============================================================
            HEADER
        ============================================================ */}
        <div className="drawer-header">
          <h5>{isEditMode ? "Edit Transport Request" : "Create Transport Request"}</h5>
          <button
            className="btn-close"
            onClick={onClose}
          ></button>
        </div>

        {/* ============================================================
            BODY
        ============================================================ */}
        <div className="drawer-body">

          <div className="mb-3">
            <label>Request Type</label>
            <select
              name="request_type"
              className="form-control"
              value={form.request_type}
              onChange={handleChange}
            >
              <option value="OUTBOUND">Outbound</option>
              <option value="INBOUND">Inbound</option>
            </select>
          </div>

          
          
              <div className="mb-3">
                <label>Reference Type</label>
                <select
                  name="reference_type"
                  className="form-control"
                  value={form.reference_type}
                  onChange={(e) => {
                    const referenceType = e.target.value;

                    setForm((prev) => ({
                      ...prev,
                      reference_type: referenceType,
                      reference_id: "",
                    }));

                    setPoSearch("");
                    setPoOptions([]);
                    setShowPoDropdown(false);
                  }}
                >
                  <option value="">Select Reference Type</option>
                  <option value="PURCHASE_ORDER">Purchase Order</option>
                  <option value="SALES_ORDER">Sales Order</option>
                  <option value="OTHERS">Others</option>
                </select>
              </div>

              <div className="mb-3 position-relative">
                <label>Reference Number</label>

                {form.reference_type === "PURCHASE_ORDER" ? (
                  <>
                    <input
                      className="form-control"
                      placeholder="Search Purchase Order number..."
                      value={
                        isEditMode &&
                        !poSearch &&
                        String(form.reference_id) ===
                          String(editTransport?.reference_id)
                          ? (editTransport?.po_no || "")
                          : poSearch
                      }
                      onChange={(e) =>
                        handlePurchaseOrderSearch(e.target.value)
                      }
                    />

                    {showPoDropdown && poOptions.length > 0 && (
                      <div className="lookup-dropdown">
                        {poOptions.map((po) => (
                          <div
                            key={po.id}
                            className="lookup-item"
                            onClick={() => {
                              setForm((prev) => ({
                                ...prev,
                                reference_id: po.id,
                              }));
                              setPoSearch(po.po_no);
                              setShowPoDropdown(false);
                              setPoOptions([]);
                            }}
                          >
                            {po.po_no} ({po.status_code})
                          </div>
                        ))}
                      </div>
                    )}

                    {showPoDropdown && poOptions.length === 0 && (
                      <div className="lookup-dropdown">
                        <div className="lookup-item">
                          No eligible Purchase Orders found
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <input
                    name="reference_id"
                    className="form-control"
                    value={form.reference_id}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        reference_id: e.target.value,
                      }))
                    }
                    placeholder="Enter reference number"
                  />
                )}
              </div>



          <div className="mb-3">
            <label>Source</label>
            <input
              name="source_name"
              className="form-control"
              value={form.source_name}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3">
            <label>Destination</label>
            <input
              name="destination_name"
              className="form-control"
              value={form.destination_name}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3">
            <label>Expected Dispatch</label>
            <input
              type="datetime-local"
              name="expected_dispatch_datetime"
              className="form-control"
              value={form.expected_dispatch_datetime}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3">
            <label>Expected Delivery</label>
            <input
              type="datetime-local"
              name="expected_delivery_datetime"
              className="form-control"
              value={form.expected_delivery_datetime}
              onChange={handleChange}
            />
          </div>

          {/* ============================================================
              TRANSPORTER LOOKUP (PO STYLE)
          ============================================================ */}
          <div className="mb-3 position-relative">
            <label>Transporter</label>

            <input
              className="form-control"
              placeholder="Search transporter..."
              value={transporterSearch}
              onChange={(e) =>
                handleTransporterSearch(e.target.value)
              }
            />

            {showDropdown && transporterOptions.length > 0 && (
              <div className="lookup-dropdown">
                {transporterOptions.map((t) => (
                  <div
                    key={t.id}
                    className="lookup-item"
                    onClick={() => {
                      setForm({
                        ...form,
                        transporter_bp_id: t.id,
                      });

                      setTransporterSearch(t.name);
                      setShowDropdown(false);
                      setTransporterOptions([]);
                    }}
                  >
                    {t.name}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* ============================================================
            FOOTER
        ============================================================ */}
        <div className="drawer-footer">
          <button
            className="btn btn-secondary me-2"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving
            ? "Saving..."
            : isEditMode
              ? "Update"
              : "Create"}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
